import { type ReactNode, useEffect } from 'react';
import { cn } from '@doccareplus/shared/lib/utils';

export interface DrawerProps {
  /** Panel heading. Also used as the dialog's accessible name. */
  title: string;
  /** Optional sub-heading under the title (e.g. the clinic name). */
  description?: string;
  onClose: () => void;
  children: ReactNode;
  /** Sticky footer actions row (buttons). */
  footer?: ReactNode;
  /** Accessible label for the close button. Defaults to "Close". */
  closeLabel?: string;
  /** When false, backdrop/Escape dismissal and the close icon are disabled. Explicit actions may still call onClose. */
  dismissible?: boolean;
  className?: string;
}

/**
 * Drawer — right-side slide-in panel (the design's `sheetIn` sheet).
 *
 * Pure presentational: renders a scrim + edge-anchored panel and forwards close
 * events. By default it closes on Escape and scrim click; protected forms can disable implicit dismissal. This is the desktop replacement for
 * centered dialogs when a form should open "from the side" — see
 * docs/design/Medrecordly.dc.html (register patient / book appointment).
 */
export function Drawer({
  title,
  description,
  onClose,
  children,
  footer,
  closeLabel = 'Close',
  dismissible = true,
  className,
}: DrawerProps) {
  useEffect(() => {
    if (!dismissible) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [dismissible, onClose]);

  return (
    <div
      role="presentation"
      onClick={dismissible ? onClose : undefined}
      className="fixed inset-0 z-[110] flex justify-end bg-black/40 backdrop-blur-sm animate-[fadeIn_0.12s]"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(event) => event.stopPropagation()}
        className={cn(
          'flex h-full w-[min(460px,100vw)] flex-col bg-[var(--surface)] shadow-[var(--shadow-3)]',
          'animate-[sheetIn_0.2s_ease-out]',
          className
        )}
      >
        <div className="flex items-center justify-between gap-3 border-b border-[var(--border)] px-[22px] py-[18px]">
          <div className="min-w-0">
            <h2 className="text-[17px] font-bold text-[var(--text)]">{title}</h2>
            {description && <p className="mt-0.5 text-[12.5px] text-[var(--text-3)]">{description}</p>}
          </div>
          {dismissible ? (
            <button
              type="button"
              aria-label={closeLabel}
              onClick={onClose}
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[8px] bg-[var(--surface-2)] text-[var(--text-2)] transition-colors hover:bg-[var(--surface-3)] hover:text-[var(--text)]"
            >
              <svg
                className="h-4 w-4"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M18 6 6 18M6 6l12 12" />
              </svg>
            </button>
          ) : null}
        </div>

        <div className="flex-1 overflow-y-auto px-[22px] py-5">{children}</div>

        {footer && (
          <div className="flex gap-2.5 border-t border-[var(--border)] px-[22px] py-4">{footer}</div>
        )}
      </div>
    </div>
  );
}
